import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../services/api";
import { StatusPill, statusColors } from "./orderKit";

import { t } from "../i18n";
export function parseNumber(v) {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

export function fmtEtb(v) {
  const n = parseNumber(v);
  return n == null
    ? "-"
    : t("common.amount_etb", { amount: n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) });
}

/** "15 minutes" → "15 min"; hides empty / zero values (like the app's RouteCard). */
function fmtEta(v) {
  if (v == null || v === "" || v === "N/A") return null;
  const n = parseNumber(v);
  if (n == null || n === 0) return null;
  return t("common.minutes_short", { n: Math.round(n) });
}

function fmtKm(v) {
  if (v == null || v === "" || v === "N/A") return null;
  const n = parseNumber(v);
  return n == null ? String(v) : t("common.km", { n: n.toLocaleString(undefined, { maximumFractionDigits: 1 }) });
}

/** Load route details + its orders, then open the route screen with the state it expects. */
export async function openRouteDetails({ route, merchantId, navigate, path }) {
  const [detailsRes, ordersRes] = await Promise.all([
    api.get(`/api/orders/routes/${route.routeId}/details`),
    api.get(`/api/orders`, { params: { merchantId } }),
  ]);
  const routeDetails = detailsRes.data;
  const ids = routeDetails?.orderedOrderIds || [];
  const routeOrders = (ordersRes.data || []).filter((order) => ids.includes(order.id));
  navigate(`${path}/${route.routeId}`, {
    state: { result: routeDetails, selectedOrders: routeOrders, merchantId },
  });
}

/** Totals for the summary strip above a route list. */
export function summarizeRouteList(routes) {
  let stops = 0;
  let km = 0;
  let pay = 0;
  for (const r of routes || []) {
    stops += r.orderedOrderIds?.length || 0;
    km += parseNumber(r.totalDistanceKm) || 0;
    pay += parseNumber(r.driverTotalEarning) || 0;
  }
  return { count: (routes || []).length, stops, km, pay };
}

export function RouteStats({ routes, payLabel }) {
  const s = summarizeRouteList(routes);
  const tiles = [
    { icon: "fa-route", label: t("route.stats.routes"), value: s.count.toLocaleString() },
    { icon: "fa-location-dot", label: t("route.stats.stops"), value: s.stops.toLocaleString() },
    {
      icon: "fa-road",
      label: t("route.stats.distance"),
      value: t("common.km", { n: s.km.toLocaleString(undefined, { maximumFractionDigits: 1 }) }),
    },
    { icon: "fa-coins", label: payLabel || t("route.stats.driver_pay"), value: fmtEtb(s.pay) },
  ];
  return (
    <div className="route-stats">
      {tiles.map((tile) => (
        <div key={tile.label} className="route-stats__tile">
          <span className="route-stats__icon">
            <i className={`fa-solid ${tile.icon}`} aria-hidden="true" />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="route-stats__label">{tile.label}</div>
            <div className="route-stats__value">{tile.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const PIPELINE = [
  { id: "awaiting", label: "route.pipeline.broadcast" },
  { id: "accepted", label: "route.pipeline.accepted" },
  { id: "dispatched", label: "route.pipeline.on_the_road" },
  { id: "completed", label: "route.pipeline.completed" },
];

/** Four-step progress track for broadcast routes. */
export function PipelineTrack({ bucket }) {
  const at = PIPELINE.findIndex((p) => p.id === bucket);
  return (
    <ol className="pipeline-track" aria-label={t("route.pipeline.aria")}>
      {PIPELINE.map((p, i) => {
        const state = at < 0 ? "todo" : i < at ? "done" : i === at ? (bucket === "completed" ? "done" : "now") : "todo";
        return (
          <li key={p.id} className={`pipeline-track__step pipeline-track__step--${state}`} aria-current={state === "now" ? "step" : undefined}>
            <span className="pipeline-track__dot">
              {state === "done" ? <i className="fa-solid fa-check" aria-hidden="true" /> : null}
            </span>
            <span className="pipeline-track__label">{t(p.label)}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** Payment chip for the ODOS service fee (routes must be paid before the pickup QR unlocks). */
export function PaymentChip({ status }) {
  const s = String(status || "").toUpperCase();
  if (!s) return null;
  const paid = s === "COMPLETED" || s === "PAID" || s === "SUCCESS";
  return (
    <span className={`tag ${paid ? "tag-success" : "tag-warning"}`}>
      <i className={`fa-solid ${paid ? "fa-circle-check" : "fa-hourglass-half"}`} aria-hidden="true" style={{ marginRight: 5 }} />
      {paid ? t("route.fee_paid") : t("route.fee_unpaid")}
    </span>
  );
}

/** One way / Round trip marker for a route; renders nothing when the API didn't say. */
export function TourModeTag({ tourMode }) {
  if (!tourMode) return null;
  const oneWay = tourMode === "ONE_WAY";
  return (
    <span
      className={`route-card__tour route-card__tour--${oneWay ? "one-way" : "round-trip"}`}
      title={oneWay ? t("tour.one_way_hint") : t("tour.round_trip_hint")}
    >
      <i className={`fa-solid ${oneWay ? "fa-arrow-right-long" : "fa-arrows-rotate"}`} aria-hidden="true" />{" "}
      {oneWay ? t("tour.one_way") : t("tour.round_trip")}
    </span>
  );
}

/**
 * App-style route card: route tile, id + status, meta row, order chips, earnings column.
 * `children` renders below (e.g. progress track / driver block).
 */
export function RouteCard({ route, statusLabel, tone, onOpen, busy, extraChips, children }) {
  const stops = route.orderedOrderIds?.length || 0;
  const km = fmtKm(route.totalDistanceKm);
  const eta = fmtEta(route.estimatedTimeMinutes);
  const ids = route.orderedOrderIds || [];
  const gross = parseNumber(route.driverTotalEarning);
  const fee = parseNumber(route.serviceFeeEtb);
  const net = parseNumber(route.netDriverPayoutEtb);

  const open = () => {
    if (!busy) onOpen(route);
  };

  return (
    <article
      className={`route-card${tone ? ` route-card--${tone}` : ""}${busy ? " route-card--busy" : ""}`}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      aria-label={t("route.open_aria", { id: route.routeId })}
    >
      <div className="route-card__main">
        <span className="route-card__icon">
          <i className={`fa-solid ${busy ? "fa-spinner fa-spin" : "fa-route"}`} aria-hidden="true" />
        </span>
        <div className="route-card__body">
          <div className="route-card__title">
            <span className="route-card__id">{t("route.title", { id: route.routeId })}</span>
            {statusLabel ? (
              <span className="status-pill" style={{ background: statusLabel.bg, color: statusLabel.fg }}>
                {statusLabel.text}
              </span>
            ) : (
              <StatusPill status={route.status} />
            )}
            {extraChips}
          </div>
          <div className="route-card__meta">
            <span>
              <i className="fa-solid fa-location-dot" aria-hidden="true" />{" "}
              {stops === 1 ? t("common.one_stop") : t("common.n_stops", { n: stops })}
            </span>
            {km ? (
              <span>
                <i className="fa-solid fa-road" aria-hidden="true" /> {km}
              </span>
            ) : null}
            {eta ? (
              <span>
                <i className="fa-regular fa-clock" aria-hidden="true" /> {eta}
              </span>
            ) : null}
            <TourModeTag tourMode={route.tourMode} />
          </div>
          {ids.length > 0 ? (
            <div className="route-card__orders">
              {ids.slice(0, 6).map((id) => (
                <span key={id} className="order-chip">
                  #{id}
                </span>
              ))}
              {ids.length > 6 ? <span className="order-chip order-chip--more">+{ids.length - 6}</span> : null}
            </div>
          ) : null}
        </div>
        <div className="route-card__money">
          {gross != null && gross > 0 ? (
            <>
              <div className="route-card__amount">{fmtEtb(gross)}</div>
              <div className="route-card__amount-label">{t("route.card.route_earning")}</div>
              {fee != null || net != null ? (
                <div className="route-card__split">
                  {t("route.card.odos_fee", { amount: fmtEtb(fee) })}
                  <br />
                  {t("route.card.direct_to_driver", { amount: fmtEtb(net) })}
                </div>
              ) : null}
            </>
          ) : (
            <div className="route-card__amount-label">{t("route.card.pricing_on_open")}</div>
          )}
        </div>
        <i className="fa-solid fa-chevron-right route-card__chevron" aria-hidden="true" />
      </div>
      {children ? (
        <div className="route-card__extra" onClick={(e) => e.stopPropagation()} role="presentation">
          {children}
        </div>
      ) : null}
    </article>
  );
}

export function routeMatchesSearch(route, q) {
  const needle = String(q || "").trim().replace(/^#/, "");
  if (!needle) return true;
  if (String(route.routeId).includes(needle)) return true;
  if ((route.orderedOrderIds || []).some((id) => String(id).includes(needle))) return true;
  if (route.driverPhone && String(route.driverPhone).includes(needle)) return true;
  return route.driverId != null && String(route.driverId) === needle;
}

/**
 * Header for the route detail screens: back link, eyebrow, "Route #id", status, stop / km / ETA chips.
 * `status` may be overridden with `statusLabel` ({ text, status }) for plain-word live states.
 */
export function RouteDetailHeader({ routeId, eyebrow, backTo, backLabel, status, statusLabel, route, children }) {
  const stops = route?.orderedOrderIds?.length || 0;
  const km = fmtKm(route?.totalDistanceKm);
  const eta = fmtEta(route?.estimatedTimeMinutes);
  const colors = statusLabel ? statusColors(statusLabel.status) : null;
  return (
    <header className="route-detail-header">
      {backTo ? (
        <Link to={backTo} className="route-detail-header__back">
          <i className="fa-solid fa-arrow-left" aria-hidden="true" /> {backLabel}
        </Link>
      ) : null}
      <div className="eyebrow">{eyebrow}</div>
      <div className="route-detail-header__title">
        <h1>{t("route.title", { id: routeId })}</h1>
        {statusLabel ? (
          <span className="status-pill" style={{ background: colors.bg, color: colors.fg }}>
            {statusLabel.text}
          </span>
        ) : status ? (
          <StatusPill status={status} />
        ) : null}
      </div>
      <div className="route-detail-header__stats">
        <span>
          <i className="fa-solid fa-location-dot" aria-hidden="true" />{" "}
          {stops === 1 ? t("common.one_stop") : t("common.n_stops", { n: stops })}
        </span>
        {km ? (
          <span>
            <i className="fa-solid fa-road" aria-hidden="true" /> {km}
          </span>
        ) : null}
        {eta ? (
          <span>
            <i className="fa-regular fa-clock" aria-hidden="true" /> {eta}
          </span>
        ) : null}
      </div>
      {children}
    </header>
  );
}

/**
 * "Step done → what's next" banner shown after the app moves the merchant on automatically
 * (e.g. Confirm route → confirmed route screen). Pass `flash` from router state: { title, next }.
 */
export function StepBanner({ flash }) {
  const [hidden, setHidden] = useState(false);
  if (!flash || hidden) return null;
  return (
    <div className="step-banner" role="status">
      <span className="step-banner__icon">
        <i className="fa-solid fa-check" aria-hidden="true" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="step-banner__title">{flash.title}</div>
        {flash.next ? (
          <div className="step-banner__next">
            <strong>{t("common.next")}</strong> {flash.next}
          </div>
        ) : null}
      </div>
      <button type="button" className="alert__close" onClick={() => setHidden(true)} aria-label={t("common.dismiss")}>
        <i className="fa-solid fa-xmark" aria-hidden="true" />
      </button>
    </div>
  );
}
